import React, { useState, useEffect } from 'react';
import { sendEmailVerification, onAuthStateChanged, type User } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { maybeSendWelcomeEmail } from '../lib/welcomeEmail';
import styles from './EmailNotVerified.module.css';

interface EmailNotVerifiedProps {
  user: User;
}

const EmailNotVerified: React.FC<EmailNotVerifiedProps> = ({ user }) => {
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Poll for email verification — detects when the user clicks the link in
  // the same tab, plus an onAuthStateChanged fallback for cross-tab verification.
  // Mirrors the logic in VerifyEmail.tsx so this component is self-sufficient
  // no matter where it's rendered from (e.g. Login.tsx).
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      await user.reload();
      if (auth.currentUser?.emailVerified) {
        clearInterval(interval);
        await maybeSendWelcomeEmail(user.uid);
        navigate('/dashboard', { replace: true });
      }
    }, 3000);

    const unsubscribe = onAuthStateChanged(auth, async (freshUser) => {
      if (freshUser?.emailVerified) {
        clearInterval(interval);
        unsubscribe();
        await maybeSendWelcomeEmail(freshUser.uid);
        navigate('/dashboard', { replace: true });
      }
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [user, navigate]);

  const handleResendVerification = async () => {
    if (resendStatus === 'sending') return;

    setResendStatus('sending');
    setError(null);

    try {
      await sendEmailVerification(user);
      setResendStatus('sent');
      setTimeout(() => setResendStatus('idle'), 5000); // Reset after 5 seconds
    } catch {
      setError('Failed to resend verification email. Please try again later.');
      setResendStatus('idle');
    }
  };

  return (
    <div className={styles.container}>
      <img src="/BodyOwn_Logo.png" alt="BodyOwn Logo" className={styles.logo} />
      <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3.006L13.732 4.94A2 2 0 0012 4.002a2 2 0 00-1.732.938L3.33 16.994c-.77 1.339.192 3.006 1.732 3.006z" />
      </svg>
      <p className={styles.message}>Email not verified. Please check your inbox for the verification link.</p>
      <p className={styles.spamNote}>If you don't see the email, please check your spam folder.</p>

      <button 
        onClick={handleResendVerification} 
        className={styles.resendButton}
        disabled={resendStatus === 'sending'}
      >
        {resendStatus === 'sending' ? 'Sending...' : 'Resend Verification Email'}
      </button>

      {resendStatus === 'sent' && (
        <p className={styles.successMessage}>A new verification email has been sent.</p>
      )}

      {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
    </div>
  );
};

export default EmailNotVerified;